import React from 'react';
import { useTranslations } from 'use-intl';
import { Navigate, Outlet } from 'react-router-dom';
import { useOnboardingGuard } from '@/hooks/useOnboardingGuard';
import { Loader2 } from 'lucide-react';

export const OnboardingGuard: React.FC = () => {
  const t = useTranslations('consultantAuth.guards');
  const {
    isLoading,
    error,
    needsCompanyOnboarding,
    needsUserOnboarding,
    canAccessApp
  } = useOnboardingGuard();

  // Log state for debugging
  console.log('OnboardingGuard state:', {
    isLoading,
    needsCompanyOnboarding,
    needsUserOnboarding,
    canAccessApp
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>{t('checkingOnboarding')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    console.error('Onboarding check error:', error);
    // Continue to app even if there's an error checking onboarding status
    return <Outlet />;
  }

  // Priority: Company onboarding first, then user onboarding
  if (needsCompanyOnboarding) {
    console.log('OnboardingGuard: Company onboarding needed, redirecting to /join-company');
    return <Navigate to="/join-company" replace />;
  }

  if (needsUserOnboarding) {
    console.log('OnboardingGuard: User onboarding needed, redirecting to /user-onboarding');
    return <Navigate to="/user-onboarding" replace />;
  }

  // If both onboarding types are complete, allow access to app
  if (canAccessApp) {
    console.log('OnboardingGuard: All onboarding complete, rendering app content');
    return <Outlet />;
  }

  // Fallback - shouldn't reach here but redirect to company onboarding just in case
  console.log('OnboardingGuard: Fallback redirect to company onboarding');
  return <Navigate to="/join-company" replace />;
};